import {
    Button,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    makeStyles,
    tokens,
    shorthands
} from '@fluentui/react-components';
import { DismissRegular } from "@fluentui/react-icons";
import { FC, useEffect, useState } from 'react';
import { useNews } from '../../../libs/hooks/useNews';
import { NewsResponse } from '../../../libs/services/NewsService';
import { useUserSetting } from '../../../libs/hooks/useUserSetting';
import { NewsRead } from "../../../libs/services/SettingsService";
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/app/store';
import { useLog } from '../../../libs/hooks';
import { LogEventType } from "../../../libs/services/LogService";
import { NewsfeedAccordion } from './NewsfeedAccordion';

const useClasses = makeStyles({
    drawer: {
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        height: '100%',
        borderLeft: `1px solid ${tokens.colorNeutralStroke1}`,
    },
    drawerHeaderTitle: {
        fontSize: `${tokens.fontSizeBase300} !important`,
    },
    drawerBody: {
        ...shorthands.padding(tokens.spacingHorizontalM),
        overflow: 'auto',
        height: '100%',
        '&::-webkit-scrollbar-track': {
            background: tokens.colorNeutralBackground3,
        },
        '&::-webkit-scrollbar-thumb': {
            background: tokens.colorNeutralForeground3,
            borderRadius: '5px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
            background: tokens.colorNeutralForeground2,
        },
    },
});

interface NewsfeedProps {
    open: boolean;
    closeable: boolean;
    onOpenChange: (open: boolean) => void;
    onUnreadCountChange: (count: number) => void;
    width?: number;
}

export const Newsfeed: FC<NewsfeedProps> = ({ open, closeable, onOpenChange, onUnreadCountChange, width }) => {
    const classes = useClasses();
    const [newsItems, setNewsItems] = useState<NewsResponse[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const news = useNews();
    const userSetting = useUserSetting();
    const [readNewsIds, setReadNewsIds] = useState<NewsRead[]>(userSetting.getNewsRead());
    const newsLastUpdated = useSelector((state: RootState) => state.app.newsLastUpdated);
    const log = useLog();
    const [openAccordionItems, setOpenAccordionItems] = useState<string[]>([]);

    useEffect(() => {
        const readNews = userSetting.getNewsRead();
        setReadNewsIds(readNews);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        void loadNews();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newsLastUpdated]);

    useEffect(() => {
        const unreadCount = newsItems.filter(
            newsItem => !readNewsIds.some(readItem => readItem.id === newsItem.id)
        ).length;
        onUnreadCountChange(unreadCount);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newsItems, readNewsIds]);

    const markNewsAsRead = (newsId: string) => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        const newsArray = readNewsIds || [];

        const filteredReadNews = newsArray.filter(readItem =>
            newsItems.some(newsItem => newsItem.id === readItem.id)
        );

        if (!filteredReadNews.some(newsRead => newsRead.id === newsId)) {
            const newNewsRead: NewsRead = {
                id: newsId,
            };

            const updatedReadNews = [...filteredReadNews, newNewsRead];
            setReadNewsIds(updatedReadNews);

            userSetting.setNewsRead(updatedReadNews);

            const currentNews = newsItems.find(news => news.id === newsId);
            const newsTitle = currentNews?.title ?? 'Unbekannt';

            log.writeLog(LogEventType.NewsRead, newsTitle).catch((_) => {});
        }
    };

    const formatDate = (dateInput: string | Date): string => {
        const date = dateInput instanceof Date ? dateInput : new Date(dateInput);

        if (isNaN(date.getTime())) {
            return "Kein Datum";
        }

        return new Intl.DateTimeFormat('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        }).format(date);
    };

    const loadNews = async () => {
        setIsLoading(true);
        try {
            const loadedNews = await news.load();
            setNewsItems(loadedNews);
        } catch (error) {
            console.error("Fehler beim Laden der News:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        void loadNews();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Drawer
            open={open}
            onOpenChange={(_event, data) => { onOpenChange(data.open); }}
            position="end"
            className={classes.drawer}
            type="inline"
            style={width ? { width: `${width}px` } : undefined}
        >
            <DrawerHeader>
                <DrawerHeaderTitle action={
                    closeable ? (
                        <Button
                            appearance="subtle"
                            aria-label="Schließen"
                            icon={<DismissRegular />}
                            onClick={() => { onOpenChange(false); }}
                        />
                    ) : null
                }>
                    <span className={classes.drawerHeaderTitle}>Newsfeed</span>
                </DrawerHeaderTitle>
            </DrawerHeader>
            <DrawerBody className={classes.drawerBody}>
                {isLoading ? (
                    <div>Lade News...</div>
                ) : newsItems.length > 0 ? (
                    <NewsfeedAccordion
                        newsItems={newsItems}
                        readNewsIds={readNewsIds}
                        openAccordionItems={openAccordionItems}
                        setOpenAccordionItems={setOpenAccordionItems}
                        markNewsAsRead={markNewsAsRead}
                        formatDate={formatDate}
                    />
                ) : (
                    <div>Keine News verfügbar</div>
                )}
            </DrawerBody>
        </Drawer>
    );
};
